import React, { useState, useEffect, useRef } from "react";
import { Button, Input } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import cloneDeep from "lodash/cloneDeep";
import { toast } from "react-toastify";
import { getSpecsUnits } from "../../../../actions/specManager";
import {
  getUnitOptions,
  computeTestSpecPreviewAdvanceState,
  globalTestSpecSpecFormBlocksSave,
  canEditSpecs,
  getTestSpecEntityLabels,
  mapEnvLocationsToProductShape,
  SPEC_TYPE,
} from "../utils/utils";
import microscopeIcon from "../../../../assets/images/product/specmanager/microscope-line.svg";
import arrowRightLineIcon from "../../../../assets/images/product/specmanager/arrow-right-line.svg";
import arrowRightIcon from "../../../../assets/images/product/specmanager/arrow-right-s-line.svg";
import pendingStatusIcon from "../../../../assets/images/product/specmanager/circle-line.svg";
import checkboxCircleFillIcon from "../../../../assets/images/product/specmanager/checkbox-circle-fill.svg";
import { CoverageSummarySection } from "../shared/specManagerUi";
import TestSpecForm from "../testSpec/TestSpecForm";
import TestSpecPreviewStep from "../testSpec/TestSpecPreviewStep";
import UpdateLocationListPanel from "./UpdateLocationListPanel";
import "../testSpec/TestSpecEdit.css";
import "../testSpec/TestSpecPreviewStep.css";

export default function EnvTestSpecEditor({
  customer,
  identifierFields,
  testName,
  specRows,
  selectedLocations,
  isPreviewStep,
  hasDraftChanges,
  locationSearchValue,
  onLocationSearchChange,
  onSpecRowsChange,
  onSelectedLocationsChange,
  onPreviewStepChange,
  onSaveClick,
  onBack,
  saving,
}) {
  const [unitOptions, setUnitOptions] = useState([]);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    getSpecsUnits(abortControllerRef.current.signal, customer).then((response) => {
      if (!response.success) {
        toast.error(response.message);
        return;
      }
      setUnitOptions(["None", ...response.result]);
    });
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [customer]);

  const previewState = computeTestSpecPreviewAdvanceState({
    loading: false,
    dataOfSpecEditing: specRows,
    testSpecEditHasDraftChanges: hasDraftChanges,
    selectedProductCount: selectedLocations.length,
    testSpecAddCountFromAllList: 0,
    testSpecSelectedCount: selectedLocations.length,
    testSpecAllProductsLoading: false,
    testSpecHasStep1Baseline: true,
  });

  const specRow = specRows[0];
  const canEdit = canEditSpecs();
  const entityLabels = getTestSpecEntityLabels(SPEC_TYPE.ENVIRONMENT);
  const productShapedSelected = mapEnvLocationsToProductShape(selectedLocations, identifierFields);

  const handleHeaderBack = isPreviewStep ? () => onPreviewStepChange(false) : onBack;

  return (
    <div className="TestSpecEdit_Root">
      <div className="TestSpecEdit_Header">
        <div className="TestSpecEdit_HeaderLeft">
          <button
            type="button"
            className="SpecManager_EditorBackButton"
            onClick={handleHeaderBack}
            aria-label="Go back"
          >
            <ArrowLeftOutlined />
          </button>
          <span className="TestSpecEdit_HeaderTitle" title={testName}>{testName}</span>
        </div>
        <div className="TestSpecEdit_HeaderRight">
          <div className="TestSpecEdit_SearchBox">
            <Input
              className="TestSpecEdit_SearchInput"
              placeholder="Search locations..."
              value={locationSearchValue}
              onChange={(event) => onLocationSearchChange(event.target.value)}
              allowClear
            />
          </div>
          <div className={`TestSpecEdit_Steps${isPreviewStep ? " TestSpecEdit_Steps--step2" : " TestSpecEdit_Steps--step1"}`}>
            <div className={`TestSpecEdit_Step${!isPreviewStep ? " TestSpecEdit_Step--active" : " TestSpecEdit_Step--completed"}`}>
              <img
                src={!isPreviewStep ? pendingStatusIcon : checkboxCircleFillIcon}
                alt=""
                className="TestSpecEdit_StepIcon"
                aria-hidden
              />
              <span>Select Locations</span>
            </div>
            <img src={arrowRightIcon} alt="" className="TestSpecEdit_StepArrow" aria-hidden />
            <div className={`TestSpecEdit_Step${isPreviewStep ? " TestSpecEdit_Step--active" : " TestSpecEdit_Step--inactive"}`}>
              <img src={pendingStatusIcon} alt="" className="TestSpecEdit_StepIcon" aria-hidden />
              <span>Preview Selection</span>
            </div>
          </div>
        </div>
      </div>

      <div className="TestSpecEdit_Body">
        {isPreviewStep ? (
          <TestSpecPreviewStep
            testName={testName}
            specRow={specRow}
            selectedProducts={productShapedSelected}
            identifierFields={identifierFields}
            searchValue={locationSearchValue}
            specType={SPEC_TYPE.ENVIRONMENT}
          />
        ) : (
          <>
            <div className="TestSpecEdit_LeftPanel">
              <TestSpecForm
                specRows={specRows}
                unitOptions={getUnitOptions(unitOptions)}
                customOnlyNoStandard={false}
                onSpecRowsChange={(updatedRows) => onSpecRowsChange(cloneDeep(updatedRows))}
              />
              <CoverageSummarySection
                products={productShapedSelected}
                specType={SPEC_TYPE.ENVIRONMENT}
              />
            </div>
            <div className="TestSpecEdit_RightPanel">
              <UpdateLocationListPanel
                customer={customer}
                identifierFields={identifierFields}
                selectedLocations={selectedLocations}
                searchValue={locationSearchValue}
                onSelectedLocationsChange={onSelectedLocationsChange}
              />
            </div>
          </>
        )}
      </div>

      <div className="TestSpecEdit_Footer">
        <div className="TestSpecEdit_FooterBadge">
          <img src={microscopeIcon} alt="" className="TestSpecEdit_FooterBadgeIcon" aria-hidden />
          <span>
            <strong>
              {selectedLocations.length}
              {" "}
              {entityLabels.plural}
            </strong>
            {" "}
            follow the standard spec
          </span>
        </div>
        <div className="TestSpecEdit_FooterActions">
          {isPreviewStep ? (
            <>
              <Button onClick={() => onPreviewStepChange(false)}>Back</Button>
              {canEdit && (
                <Button
                  type="primary"
                  loading={saving}
                  disabled={globalTestSpecSpecFormBlocksSave(specRows) || !hasDraftChanges}
                  onClick={onSaveClick}
                >
                  Save Spec
                </Button>
              )}
            </>
          ) : (
            <>
              <Button onClick={onBack}>Cancel</Button>
              <Button
                type="primary"
                disabled={previewState.previewPrimaryDisabled}
                title={previewState.previewSelectionTooltip}
                onClick={() => onPreviewStepChange(true)}
              >
                <span className="TestSpecEdit_FooterPrimaryLabel">
                  Preview Selection
                  <img src={arrowRightLineIcon} alt="" className="TestSpecEdit_FooterPrimaryArrow" aria-hidden />
                </span>
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
